import { passiveSupport } from "passive-events-support/src/utils";
passiveSupport({ events: ["touchstart"] });
// import required from vue
import {
  defineCustomElement,
  createApp,
  getCurrentInstance,
  onMounted,
  onUnmounted,
} from "vue";

// import Quasar Language and Quasar icon set
import quasarLang from "quasar.lang";
import quasarIconSet from "quasar.iconset";

// import Quasar and Quasar directives
import { Quasar, Ripple, Notify, Screen } from "quasar.src";

// import of CustomElements Root Component for each widget
import Dataset from "./Dataset.ce.vue";
import DatasetCard from "./DatasetCard.ce.vue";
import Datafile from "./Datafile.ce.vue";
import DatafileCard from "./DatafileCard.ce.vue";
import Millesime from "./Millesime.ce.vue";
import Download from "./Download.ce.vue";
import Pagination from "./Pagination.ce.vue";
import Explore from "./Explore.ce.vue";
import Apidoc from "./Apidoc.ce.vue";
import Catalog from "./Catalog.ce.vue";

// Import icon libraries (inline for importing without create tag style )
// import dsFr from "@gouvfr/dsfr/dist/dsfr.css?inline";
import quasarMdiV6 from "@quasar/extras/mdi-v6/mdi-v6.css?inline";

// Import app sass (inline for importing without create tag style )
import appSass from "./app.sass?inline";

// Function for extracting FontFace rules and other Css rules
const extractFontFacesAndCss = (cssString) => {
  const fontFacesSearchResult = cssString.match(/@font-face\s*{(?:[^{]|\n)*}/g);
  const cssRules = fontFacesSearchResult
    ? fontFacesSearchResult.reduce(
        (acc, fontFaceRule) => acc.replace(fontFaceRule, ""),
        cssString,
      )
    : cssString;
  const fontFacesRules = fontFacesSearchResult
    ? fontFacesSearchResult.join("\n")
    : "";
  return { fontFacesRules, cssRules };
};

// separate font-face and other css rules for each css files
// const {
//   fontFacesRules: dsFrFontFacesRules,
//   cssRules: dsFrCssRules,
// } = extractFontFacesAndCss(dsFr);
const {
  fontFacesRules: quasarMdiV6FontFacesRules,
  cssRules: quasarMdiV6CssRules,
} = extractFontFacesAndCss(quasarMdiV6);
const { fontFacesRules: appSassFontFacesRules, cssRules: appSassCssRules } =
  extractFontFacesAndCss(appSass);

// Wrapper for injection of Quasar plugin and fonts for our customs elements
const defineCustomElementWrapped = (component) =>
  defineCustomElement({
    name: component.name,
    components: component.components,
    styles: component.styles || [],
    props: component.props,
    emits: ["widget-mounted", "widget-unmounted", ...component.emits],
    template: component.template,
    // a setup function for rendering a wrapper component with quasar plugin and styles of each Vue sub components injected in shadow dom
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      // when Root component (= custom element) is mounted
      onMounted(() => {
        emit(["widget-mounted"], { type: getCurrentInstance().type.name });
        // we ensure quasar required webfonts are injected in document head
        useQuasarAsChildApp.ensureQuasarRequiredFontsInDocumentHead();
        // we ensure quasar css rules are injected in shadow dom
        useQuasarAsChildApp.ensureQuasarRequiredStylesInjectedInShadowDom(
          getCurrentInstance().vnode.el,
        );

        useQuasarAsChildApp.movePlugins(getCurrentInstance().vnode.el);
      });

      // when Root component (= custom element) is unmounted
      onUnmounted(() => {
        emit(["widget-unmounted"], { type: getCurrentInstance().type.name });
      });

      // ensure Quasar as plugin
      useQuasarAsChildApp.usePlugin(instance);
      Screen.setSizes({ sm: 576, md: 768, lg: 992, xl: 1248 });

      // ensure our child components styles are injected in shadow dom
      useChildComponentStyleInShadowDomMixin(instance);
    },
  });

// app plugin for injecting quasar in custom element context
const useQuasarAsChildApp = {
  // Function for use Quasar as plugin of parent app
  usePlugin: (parentApp) => {
    // create a Vue3 app (temporary app)
    const quasarApp = createApp();
    console.log("Quasar temporary app created");

    // Configure Quasar as plugin Vue3 app
    quasarApp.use(Quasar, {
      config: {},
      plugins: { Notify },
      lang: quasarLang,
      iconSet: quasarIconSet,
      directives: { Ripple },
    });
    console.log("quasar plugin injected in Quasar app");

    // We inject the context of temporary Vue3 app with quasar plugin in Root component (= custom element) context
    Object.assign(parentApp.appContext, quasarApp._context);
    console.log("context of Quasar app is injected in Custom Element App");
  },
  // Function to ensure quasar required fonts are injected in document head
  ensureQuasarRequiredFontsInDocumentHead: () => {
    // if quasar required fonts are already injected we do not need to inject them a second time
    if (!document.getElementById("dido-widgets-quasar-fontface-style")) {
      const head = document.head || document.getElementsByTagName("head")[0];
      const quasarStyleTag = document.createElement("style");
      quasarStyleTag.id = "dido-widgets-quasar-fontface-style";
      quasarStyleTag.textContent =
        // dsFrFontFacesRules +
        quasarMdiV6FontFacesRules + appSassFontFacesRules;
      head.append(quasarStyleTag);
    }
    console.log("quasar required fonts injected in document head");
  },
  // Function to ensure quasar css rules are injected in shadow dom
  ensureQuasarRequiredStylesInjectedInShadowDom: (appEl) => {
    // set id for quasar style tag in shadow dom
    const quasarStyleTagId = "quasar_style";
    // get shadow root dom element
    const shadowRoot = appEl.parentNode;
    // we inject style if the style tag for quasar doesn't ever exist
    if (!shadowRoot.getElementById(quasarStyleTagId)) {
      const quasarStyleTag = document.createElement("style");
      // set id of quasar style tag
      quasarStyleTag.id = quasarStyleTagId;
      // set content of quasar style tag
      quasarStyleTag.textContent = [
        // dsFrCssRules,
        quasarMdiV6CssRules,
        appSassCssRules,
      ]
        // css minification
        .join("")
        .replace(/\n/g, "");
      // Prepending styles (because global styles must be loaded first in shadow root)
      shadowRoot.prepend(quasarStyleTag);
    }
    console.log("quasar required css rules injected in shadow dom");
  },
  // Function to ensure dom element created in document.body move to shadow dom
  movePlugins: (appEl) => {
    // Loading Bar
    const loadinBarEl = document.getElementById("q-loading-bar");
    if (loadinBarEl) appEl.appendChild(loadinBarEl);

    // Notify
    const notifyEl = document.getElementById("q-notify");
    if (notifyEl) appEl.after(notifyEl);
  },
};

// app plugin with mixin for injecting styles of child components in shadow root of custom element
const useChildComponentStyleInShadowDomMixin = (app) => {
  app.appContext.mixins.push({
    mounted() {
      // console.log(`Vue component ${this.$.type.name} mounted`)

      // when the component is mounted we ensure that styles for component type are injected in shadow dom
      if (this.$.type.name && this.$.type.name.substr(0, 4) === "Dido") {
        // set id for style tag of component type
        const componentStyleTagId = `${this.$.type.name}_style`;
        // get shadow root dom element
        const shadowRoot = this.$root.$el.parentNode;

        // get style for text content
        let textContent = "";
        if (this.$.type.styles) {
          textContent = this.$.type.styles
            // filter rules without dido
            .map((rules) =>
              (rules.match(/[^{}\n]*dido[^{]*{[^}]*}\n?/gm) || []).join(""),
            )
            .map((rules) =>
              rules
                .split("}")
                .map((oneRule) => oneRule.trim())
                .filter((oneRule) => oneRule)
                .map((oneRule) => {
                  const [selectors, cssRule] = oneRule.split("{");
                  return [
                    selectors
                      .split(",")
                      .filter((selector) => selector.includes("dido"))
                      .join(","),
                    cssRule,
                  ].join("{");
                })
                .join("}"),
            )
            .map((rules) => `${rules}}`)
            .join("")
            .replace(/\n/g, "");
        }

        // check if element for style exist in shadow dom
        let componentStyleTag = shadowRoot.getElementById(componentStyleTagId);

        // if style tag exist and text content is empty => remove style tag and return
        if (componentStyleTag && !textContent)
          return componentStyleTag.remove();

        // if style tag exist and text content is  not empty => we replace content of style tag an return
        if (componentStyleTag && textContent)
          return (componentStyleTag.textContent = textContent);

        // if style tag doesn't exist and text content is empty => nothing to do
        if (!componentStyleTag && !textContent) return;

        // if style tag doesn't exist and text content is not empty => we create it with content
        componentStyleTag = document.createElement("style");
        componentStyleTag.id = componentStyleTagId;
        componentStyleTag.textContent = textContent;

        // Appending styles (because component styles must be loaded last in shadow root)
        shadowRoot.append(componentStyleTag);
      }
    },
    // when a Vue component is unmounted
    unmounted() {
      // console.log(`Vue component ${this.$.type.name} unmounted`)
    },
  });
};

// Available components with the default tag for registration
const availableComponents = [
  { name: "dataset", defaultTag: "dido-dataset" },
  { name: "dataset-card", defaultTag: "dido-dataset-card" },
  { name: "datafile-card", defaultTag: "dido-dataset-card" },
  { name: "datafile", defaultTag: "dido-datafile" },
  { name: "millesime", defaultTag: "dido-millesime" },
  { name: "download", defaultTag: "dido-download" },
  { name: "pagination", defaultTag: "dido-pagination" },
  { name: "explore", defaultTag: "dido-explore" },
  { name: "apidoc", defaultTag: "dido-apidoc" },
  { name: "catalog", defaultTag: "dido-catalog" },
];

// function to get spec of a web component by name
const getComponentSpecByName = (nameToCheck) =>
  availableComponents.find(({ name }) => name === nameToCheck);

// export a function for getting custom Element
export function getComponent(name) {
  if (!getComponentSpecByName(name))
    throw new Error(`No component with name ${name}`);
  if (name === "dataset") return defineCustomElementWrapped(Dataset);
  if (name === "dataset-card") return defineCustomElementWrapped(DatasetCard);
  if (name === "datafile") return defineCustomElementWrapped(Datafile);
  if (name === "datafile-card") return defineCustomElementWrapped(DatafileCard);
  if (name === "millesime") return defineCustomElementWrapped(Millesime);
  if (name === "download") return defineCustomElementWrapped(Download);
  if (name === "pagination") return defineCustomElementWrapped(Pagination);
  if (name === "explore") return defineCustomElementWrapped(Explore);
  if (name === "apidoc") return defineCustomElementWrapped(Apidoc);
  if (name === "catalog") return defineCustomElementWrapped(Catalog);
}

// export a function for registering a custom element as web component with tag name
export function register(name, tag = null) {
  const didoComponent = getComponent(name);
  const definedTag = tag || getComponentSpecByName(name).defaultTag;
  customElements.define(definedTag, didoComponent);
}
